import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Save, ArrowLeft, X, ChevronUp, ChevronDown, Plus } from "lucide-react";
import { blogApi, uploadImage } from "./adminApi";
import { SITE_URL } from "../components/Siteconfig";
import RichTextEditor from "./RichTextEditor";
import { isEmptyHtml } from "../utils/richText";
import "./Admin.css";

const emptyForm = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  coverImage: "",
  images: [], // carousel / gallery images, in display order
  author: "What Next Edu Team",
  category: "",
  tags: "",
  status: "draft",
  seoTitle: "",
  seoDescription: "",
};

function blogToForm(blog) {
  return {
    title: blog.title || "",
    slug: blog.slug || "",
    excerpt: blog.excerpt || "",
    content: blog.content || "",
    coverImage: blog.coverImage || "",
    images: Array.isArray(blog.images) ? blog.images : [],
    author: blog.author || "What Next Edu Team",
    category: blog.category || "",
    tags: (blog.tags || []).join(", "),
    status: blog.status || "draft",
    seoTitle: blog.seoTitle || "",
    seoDescription: blog.seoDescription || "",
  };
}

function formToPayload(form) {
  return {
    title: form.title.trim(),
    slug: form.slug.trim(),
    excerpt: form.excerpt.trim(),
    content: form.content,
    coverImage: form.coverImage.trim(),
    images: form.images.map((url) => url.trim()).filter(Boolean),
    author: form.author.trim() || "What Next Edu Team",
    category: form.category.trim(),
    tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    status: form.status,
    seoTitle: form.seoTitle.trim(),
    seoDescription: form.seoDescription.trim(),
  };
}

// slugify preview only — the backend is the source of truth and re-slugs
// on save, this just gives the admin a live preview of the canonical URL.
function slugPreview(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function BlogForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;

    async function load() {
      try {
        const data = await blogApi.listAll();
        const post = data.find((b) => b._id === id);
        if (!post) throw new Error("Blog post not found.");
        if (!cancelled) setForm(blogToForm(post));
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleCoverUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const url = await uploadImage(file);
      update("coverImage", url);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  // ── Carousel / gallery images ──────────────────────────────────────
  // Admin can add images either by uploading files (multi-select) or by
  // pasting a URL. Each image can be reordered (controls display order in
  // the public carousel) or removed.

  async function handleGalleryUpload(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingGallery(true);
    setError("");
    try {
      const uploaded = [];
      for (const file of files) {
        // Sequential so a single failed upload doesn't abandon the rest
        // mid-flight in an unpredictable order.
        // eslint-disable-next-line no-await-in-loop
        const url = await uploadImage(file);
        uploaded.push(url);
      }
      setForm((prev) => ({ ...prev, images: [...prev.images, ...uploaded] }));
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingGallery(false);
      e.target.value = "";
    }
  }

  function addImageUrl() {
    setForm((prev) => ({ ...prev, images: [...prev.images, ""] }));
  }

  function updateImageUrl(index, value) {
    setForm((prev) => {
      const images = [...prev.images];
      images[index] = value;
      return { ...prev, images };
    });
  }

  function removeImage(index) {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  }

  function moveImage(index, direction) {
    setForm((prev) => {
      const images = [...prev.images];
      const target = index + direction;
      if (target < 0 || target >= images.length) return prev;
      [images[index], images[target]] = [images[target], images[index]];
      return { ...prev, images };
    });
  }

  async function handleSubmit(e, publish) {
    e.preventDefault();
    setError("");

    if (!form.title.trim()) {
      setError("Title is required.");
      return;
    }
    if (isEmptyHtml(form.content)) {
      setError("Content is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (publish !== undefined) payload.status = publish ? "published" : "draft";

      if (isEdit) {
        await blogApi.update(id, payload);
      } else {
        await blogApi.create(payload);
      }
      navigate("/admin/blogs");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p>Loading…</p>;

  const canonicalPreview = `${SITE_URL}/blog/${form.slug ? slugPreview(form.slug) : slugPreview(form.title) || "your-post-slug"}`;

  return (
    <div>
      <div className="admin-topbar">
        <h1>{isEdit ? "Edit Post" : "New Post"}</h1>
        <Link to="/admin/blogs" className="admin-btn admin-btn--outline" style={{ width: "auto" }}>
          <ArrowLeft size={16} /> Back to Blog Posts
        </Link>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <form className="admin-form" onSubmit={(e) => handleSubmit(e, undefined)}>
        <div className="admin-form__section">
          <h3>Post Details</h3>

          <div className="admin-field">
            <label>Title *</label>
            <input value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="Post title" required />
          </div>

          <div className="admin-form__row">
            <div className="admin-field">
              <label>Slug (auto-generated if left blank)</label>
              <input value={form.slug} onChange={(e) => update("slug", e.target.value)} placeholder="e.g. 5-tips-to-learn-python" />
              <span className="admin-field__hint">
                Canonical URL: <code>{canonicalPreview}</code> — unique to this post; every post is guaranteed one
                distinct canonical URL by its slug.
              </span>
            </div>
            <div className="admin-field">
              <label>Category</label>
              <input value={form.category} onChange={(e) => update("category", e.target.value)} placeholder="e.g. Career Tips" />
            </div>
          </div>

          <div className="admin-form__row">
            <div className="admin-field">
              <label>Author</label>
              <input value={form.author} onChange={(e) => update("author", e.target.value)} />
            </div>
            <div className="admin-field">
              <label>Tags (comma-separated)</label>
              <input value={form.tags} onChange={(e) => update("tags", e.target.value)} placeholder="python, career, tips" />
            </div>
          </div>

          <div className="admin-field">
            <label>Excerpt (short teaser for the blog listing)</label>
            <textarea value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} rows={2} maxLength={400} />
          </div>

          <div className="admin-field">
            <label>Cover Image (blog-listing thumbnail)</label>
            <input type="file" accept="image/*" onChange={handleCoverUpload} disabled={uploading} />
            {uploading && <span style={{ fontSize: "0.8rem", color: "var(--c-mut)" }}>Uploading…</span>}
            {form.coverImage && <img src={form.coverImage} alt="Cover preview" className="admin-thumb-preview" />}
            <input
              value={form.coverImage}
              onChange={(e) => update("coverImage", e.target.value)}
              placeholder="or paste an image URL directly"
              style={{ marginTop: "0.5rem" }}
            />
            <span className="admin-field__hint">
              If left blank, the first gallery image below is used automatically.
            </span>
          </div>

          <div className="admin-field">
            <label>Image Carousel / Gallery (shown on the post page)</label>
            <input type="file" accept="image/*" multiple onChange={handleGalleryUpload} disabled={uploadingGallery} />
            {uploadingGallery && <span style={{ fontSize: "0.8rem", color: "var(--c-mut)" }}>Uploading…</span>}

            {form.images.length > 0 && (
              <ul className="admin-gallery-list">
                {form.images.map((url, i) => (
                  <li key={i} className="admin-gallery-list__item">
                    {url ? (
                      <img src={url} alt={`Gallery ${i + 1}`} className="admin-thumb-preview admin-thumb-preview--sm" />
                    ) : (
                      <div className="admin-thumb-preview admin-thumb-preview--sm admin-thumb-preview--empty" />
                    )}
                    <input
                      value={url}
                      onChange={(e) => updateImageUrl(i, e.target.value)}
                      placeholder="Image URL"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="admin-icon-btn"
                      onClick={() => moveImage(i, -1)}
                      disabled={i === 0}
                      aria-label="Move up"
                      title="Move up"
                    >
                      <ChevronUp size={16} />
                    </button>
                    <button
                      type="button"
                      className="admin-icon-btn"
                      onClick={() => moveImage(i, 1)}
                      disabled={i === form.images.length - 1}
                      aria-label="Move down"
                      title="Move down"
                    >
                      <ChevronDown size={16} />
                    </button>
                    <button
                      type="button"
                      className="admin-icon-btn admin-icon-btn--danger"
                      onClick={() => removeImage(i)}
                      aria-label="Remove image"
                      title="Remove"
                    >
                      <X size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <button type="button" className="admin-btn admin-btn--outline" style={{ width: "auto", marginTop: "0.5rem" }} onClick={addImageUrl}>
              <Plus size={16} /> Add image URL
            </button>
            <span className="admin-field__hint">
              Drag order with the up/down arrows — this is the order visitors see in the carousel on the post page.
            </span>
          </div>

          <div className="admin-field">
            <label>Content *</label>
            <RichTextEditor
              mode="full"
              value={form.content}
              onChange={(html) => update("content", html)}
              placeholder="Write your post here. Select text and click B to make it bold."
              minHeight={320}
            />
            <span className="admin-field__hint">
              Use the toolbar for bold, italic, underline, headings, lists, quotes and links. The
              &lt;/&gt; button lets you edit the HTML directly (e.g. to add an image).
            </span>
          </div>
        </div>

        <div className="admin-form__section">
          <h3>SEO (optional)</h3>
          <div className="admin-field">
            <label>SEO Title</label>
            <input
              value={form.seoTitle}
              onChange={(e) => update("seoTitle", e.target.value)}
              placeholder={form.title || "Falls back to the post title"}
            />
          </div>
          <div className="admin-field">
            <label>SEO Description</label>
            <textarea
              value={form.seoDescription}
              onChange={(e) => update("seoDescription", e.target.value)}
              rows={2}
              placeholder={form.excerpt || "Falls back to the excerpt"}
            />
          </div>
          <span className="admin-field__hint">
            These drive the page &lt;title&gt;, meta description and social-share preview for this post. They take
            effect immediately client-side, and are baked into the pre-rendered <code>index.html</code> for this
            post the next time the site is built — see <code>scripts/generate-static-pages.js</code>.
          </span>
        </div>

        <div className="admin-form__actions">
          <button type="submit" className="admin-btn admin-btn--outline" disabled={saving} style={{ width: "auto" }}>
            <Save size={16} /> {saving ? "Saving…" : form.status === "published" ? "Save Changes" : "Save Draft"}
          </button>
          <button
            type="button"
            className="admin-btn admin-btn--primary"
            style={{ width: "auto" }}
            disabled={saving}
            onClick={(e) => handleSubmit(e, true)}
          >
            {saving ? "Publishing…" : "Publish"}
          </button>
          <Link to="/admin/blogs" className="admin-btn admin-btn--outline">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}